import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './index.scss'
import {
	FancyNumber,
	useFancyNumber,
} from 'selfServicePortal/components/FancyNumber'
import SspContext from 'selfServicePortal/contexts/SspContext'
import { ICreditBalance } from 'selfServicePortal/interfaces/i-api-creaditCard'
import { useMutation } from '@tanstack/react-query'
import { decreaseLimit } from 'selfServicePortal/services/LimitService/decreaseLimit'
import { Message } from 'selfServicePortal/components/Message/Message'
import { menuItems } from 'selfServicePortal/constants/menuItems'
import IncreaseLimitForm from './IncreaseLimitForm'
import { getErrorMessage } from 'selfServicePortal/utils'
import { Screen } from 'selfServicePortal/components/ui/Screen'
import { Card } from 'selfServicePortal/components/ui/Card'
import { Button } from 'selfServicePortal/components/ui/Button'
import { SpinnerScreen } from 'selfServicePortal/components/ui/Spinner'
import { getCurrency } from 'shared'
import { useNavigate } from 'react-router'

export default function ChangeCreditLimit() {
	const navigate = useNavigate()
	const [showIncreaseForm, setShowIncreaseForm] = useState(false)
	const { overviewData, customerData, changeComponent } =
		useContext(SspContext)
	const fancyNumber = useFancyNumber({
		minNumber: 30000,
		maxNumber: 150000,
		step: 10000,
		initialNumber: (overviewData as ICreditBalance).creditLimit,
	})
	const { t } = useTranslation()
	const decreaseLimitMutation = useMutation({
		mutationFn: decreaseLimit,
	})
	const action =
		fancyNumber.number > fancyNumber.initialNumber ? 'increase' : 'decrease'

	function handleSubmit() {
		if (action === 'increase') {
			setShowIncreaseForm(true)
		} else if (action === 'decrease') {
			decreaseLimitMutation.mutate({
				desiredLimit: fancyNumber.number,
				ssn: String(customerData?.nationalId),
				email: String(customerData?.email),
				mobileNumber: String(customerData?.mobilePhoneNumber),
				partyId: String(customerData?.customerId),
			})
		}
	}

	if (decreaseLimitMutation.isLoading) {
		return <SpinnerScreen message={t('ChangeCreditLimitLoading')} />
	}

	if (
		decreaseLimitMutation.isError ||
		(decreaseLimitMutation.isSuccess &&
			decreaseLimitMutation.data.decision !== 'Approved')
	) {
		const errorMessage = decreaseLimitMutation.isError
			? getErrorMessage(decreaseLimitMutation.error)
			: {
					Approved: t(''),
					Investigation: t('ChangeCreditLimitInvestigation'),
					Rejected: t('ChangeCreditLimitDenied'),
					Invalid: t('ChangeCreditLimitInvalid'),
					Error: t(''),
			  }[decreaseLimitMutation.data.decision]

		return (
			<Screen>
				<Card transperentBackground>
					<Card.Content>
						<Message type="fail" message={errorMessage} />
					</Card.Content>
					<Card.Footer>
						<Button
							variant="primary"
							onClick={() => {
								navigate(0)
							}}>
							{t('ChangeCreditLimitContinue')}
						</Button>
					</Card.Footer>
				</Card>
			</Screen>
		)
	}

	if (decreaseLimitMutation.isSuccess) {
		return (
			<Screen>
				<Card transperentBackground>
					<Card.Content>
						<Message
							type="success"
							message={t('ChangeCreditLimitSuccess')}
						/>
					</Card.Content>
					<Card.Footer>
						<Button
							variant="primary"
							onClick={() => {
								changeComponent(menuItems[0].items[0])
							}}>
							{t('ChangeCreditLimitContinue')}
						</Button>
					</Card.Footer>
				</Card>
			</Screen>
		)
	}

	if (showIncreaseForm) {
		return <IncreaseLimitForm desiredLimit={fancyNumber.number} />
	}
	return (
		<Screen className="change-credit-limit ">
			<Card>
				<Card.Header>{t('ChangeCreditLimitTitle')}</Card.Header>
				<Card.Content>
					<section>
						<div className="info">
							<span>{t('ChangeCreditLimitCurrentLimit')}</span>
							<span>
								{`${
									(overviewData as ICreditBalance).creditLimit
								} ${getCurrency()}`}
							</span>
						</div>
					</section>

					<FancyNumber>
						<FancyNumber.InputLabel>
							{t('ChangeCreditLimitDesiredLimit')}
						</FancyNumber.InputLabel>
						<FancyNumber.Input {...fancyNumber} />
						<FancyNumber.Slider {...fancyNumber} />
						<FancyNumber.SliderLabel
							left={`${getCurrency()} ${fancyNumber.minNumber}`}
							right={`${getCurrency()} ${fancyNumber.maxNumber}`}
						/>
					</FancyNumber>

					<Card.Footer>
						<Button
							variant="secondary"
							disabled={
								fancyNumber.number === fancyNumber.initialNumber
							}
							onClick={handleSubmit}>
							{action === 'decrease'
								? t('ChangeCreditLimitConfirm')
								: t('ChangeCreditLimitContinue')}
						</Button>
					</Card.Footer>
				</Card.Content>
			</Card>
		</Screen>
	)
}
